import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';

const QuotationConfigTab = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/quotations/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Error loading config:', error);
      setMessage('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/quotations/config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage('✅ Configuración guardada exitosamente');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving config:', error);
      setMessage('❌ Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handlePriceChange = (index, field, value) => {
    const newPriceTable = [...config.price_table];
    newPriceTable[index][field] = field === 'price_without_iva' ? parseInt(value) || 0 : value;
    setConfig({...config, price_table: newPriceTable});
  };

  const addPriceRange = () => {
    const newPriceTable = [...config.price_table, {
      range: "9-15",
      price_without_iva: 20990,
      description: "9-15 unidades"
    }];
    setConfig({...config, price_table: newPriceTable});
  };

  const removePriceRange = (index) => {
    const newPriceTable = config.price_table.filter((_, i) => i !== index);
    setConfig({...config, price_table: newPriceTable});
  };

  const handleCCEmailChange = (index, value) => {
    const newEmails = [...config.admin_cc_emails];
    newEmails[index] = value;
    setConfig({...config, admin_cc_emails: newEmails});
  };

  if (loading) {
    return <div className="p-6">Cargando configuración...</div>;
  }

  if (!config) {
    return <div className="p-6">Error al cargar configuración</div>;
  }

  return (
    <div className="space-y-8">
      {message && (
        <div className={`p-4 rounded-lg ${message.includes('✅') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message}
        </div>
      )}

      {/* Folio Counter */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold mb-4">📋 Contador de Folios</h3>
        <div className="space-y-2">
          <p className="text-sm text-gray-600">
            Folio actual: <span className="font-bold text-xl text-blue-600">COT-{config.current_folio}</span>
          </p>
          <p className="text-xs text-gray-500">
            La próxima cotización será: COT-{config.current_folio + 1}
          </p>
        </div>
      </div>

      {/* Price Table */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">💰 Tabla de Precios por Volumen</h3>
          <button
            onClick={addPriceRange}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm"
          >
            + Agregar Rango
          </button>
        </div>
        
        <div className="space-y-3">
          {config.price_table.map((price, index) => (
            <div key={index} className="grid grid-cols-12 gap-3 items-center border-b pb-3">
              <div className="col-span-3">
                <label className="text-xs text-gray-500">Rango</label>
                <input
                  type="text"
                  value={price.range}
                  onChange={(e) => handlePriceChange(index, 'range', e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder="1 o 4-8"
                />
              </div>
              <div className="col-span-4">
                <label className="text-xs text-gray-500">Precio sin IVA</label>
                <input
                  type="number"
                  value={price.price_without_iva}
                  onChange={(e) => handlePriceChange(index, 'price_without_iva', e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder="26990"
                />
              </div>
              <div className="col-span-4">
                <label className="text-xs text-gray-500">Descripción</label>
                <input
                  type="text"
                  value={price.description}
                  onChange={(e) => handlePriceChange(index, 'description', e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder="1 unidad"
                />
              </div>
              <div className="col-span-1 pt-5">
                {config.price_table.length > 1 && (
                  <button
                    onClick={() => removePriceRange(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-3">
          * El IVA (19%) se calcula automáticamente al generar la cotización
        </p>
      </div>

      {/* Email Configuration */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold mb-4">📧 Configuración de Emails</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Asunto del Email</label>
            <input
              type="text"
              value={config.email_subject_template}
              onChange={(e) => setConfig({...config, email_subject_template: e.target.value})}
              className="w-full border rounded px-3 py-2"
              placeholder="Cotización Contact — COT-{folio}"
            />
            <p className="text-xs text-gray-500 mt-1">Variables: {'{'}folio{'}'}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Cuerpo del Email</label>
            <textarea
              value={config.email_body_template}
              onChange={(e) => setConfig({...config, email_body_template: e.target.value})}
              rows="6"
              className="w-full border rounded px-3 py-2 font-mono text-sm"
              placeholder="Estimado/a {contact_person}..."
            />
            <p className="text-xs text-gray-500 mt-1">Variables: {'{'}contact_person{'}'}, {'{'}folio{'}'}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Emails CC (Administradores)</label>
            <div className="space-y-2">
              {config.admin_cc_emails.map((email, index) => (
                <input
                  key={index}
                  type="email"
                  value={email}
                  onChange={(e) => handleCCEmailChange(index, e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="admin@contactnetwork.cl"
                />
              ))}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Texto Intro PDF</label>
            <textarea
              value={config.pdf_intro_text}
              onChange={(e) => setConfig({...config, pdf_intro_text: e.target.value})}
              rows="3"
              className="w-full border rounded px-3 py-2"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Delay de Envío (minutos)</label>
              <input
                type="number"
                value={config.send_delay_minutes}
                onChange={(e) => setConfig({...config, send_delay_minutes: parseInt(e.target.value) || 3})}
                className="w-full border rounded px-3 py-2"
                min="1"
                max="60"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Validez (días)</label>
              <input
                type="number"
                value={config.validity_days}
                onChange={(e) => setConfig({...config, validity_days: parseInt(e.target.value) || 14})}
                className="w-full border rounded px-3 py-2"
                min="1"
                max="90"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50"
        >
          {saving ? 'Guardando...' : 'Guardar Configuración'}
        </button>
      </div>
    </div>
  );
};

export default QuotationConfigTab;
